import { useQueryClient } from '@tanstack/react-query';
import { Buffer } from 'buffer';
import { useEffect, useLayoutEffect, useRef } from 'react';

import { useClient, useFormContext } from '@/hooks';
import { toFileId } from '@/hooks/api';
import { useDocumentStore } from '@/store';
import { toAnnotations, TokenCredentials } from '@/utils';

const isEmptyForm = (values?: Record<string, unknown>) => {
  return Object.keys(values ?? {}).length === 0;
};

export function useAutoUpdateAnnotations() {
  const fieldConfig = useDocumentStore((state) => state.fieldConfig);
  const formState = useFormContext().state;

  const documentId = useDocumentStore((state) => state.documentId);
  const annotationFileUrl = useDocumentStore((state) => state.annotationFileUrl);
  const isEditing = useDocumentStore((state) => state.isEditing);

  const idempotencyRef = useRef<string | null>(null);
  const { client } = useClient();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!isEditing) return;

    const formValues = formState?.values;
    if (isEmptyForm(formValues)) return;

    if (fieldConfig && documentId && annotationFileUrl) {
      const annotations = JSON.stringify(toAnnotations(fieldConfig, formValues));
      const idempotency = `${annotationFileUrl}-${annotations}`;
      if (idempotency === idempotencyRef.current) return;

      const contentType = 'application/json';
      const blob = new Blob([annotations], { type: contentType });

      blob.arrayBuffer().then((data) => {
        const content = Buffer.from(data);
        const headers = { 'Content-Type': contentType };
        client.makeFileServerPutRequest(annotationFileUrl, content, { headers });
        idempotencyRef.current = idempotency;
      });
    }
  }, [client, annotationFileUrl, documentId, fieldConfig, formState?.values, isEditing]);

  useLayoutEffect(() => {
    return () => {
      queryClient
        .invalidateQueries({
          queryKey: [(client.credentials as TokenCredentials).organizationId, 'files'],
          exact: true,
        })
        .catch(console.error);
      queryClient
        .invalidateQueries({
          queryKey: [(client.credentials as TokenCredentials).organizationId, 'files', toFileId(annotationFileUrl)],
          exact: true,
        })
        .catch(console.error);
    };
  }, [client.credentials, annotationFileUrl, queryClient]);
}
